import { ReactNode, useEffect, useState } from 'react';

import { BadgeSubscription } from '@components/badge-subscription';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { SortableList, DraggableComponentProps } from '@components/drag-and-drop/sortable-list';
import { RouterTab, RouterTabsHeader } from '@components/header/router-tabs-header';
import { IconButton, IconButtonProps } from '@components/icon-button';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconApi } from '@components/icons/api';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDropdown } from '@components/icons/dropdown';
import { IconEndpoint } from '@components/icons/endpoint';
import { IconSettings } from '@components/icons/settings';
import { IconSSO } from '@components/icons/sso';
import { IconUpload } from '@components/icons/upload';
import { IconWebhook } from '@components/icons/webhook';
import { Link } from '@components/link';
import { ListCard } from '@components/list-card';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { MenuItem, MenuSeparator } from '@components/menu/menu';
import { PageContent } from '@components/page-containers/page-container';
import { MiddleColumnPage } from '@components/page-containers/page-containers';
import { RadioGroup } from '@components/radio-group';
import { Stack } from '@components/stack';
import { StepWizard } from '@components/step-wizard';
import { Text } from '@components/text';
import { showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { reorderList } from '@utils/collections';

import { combineEndpointsAndWebhooks, IntegrationItem } from './common';
import { EndpointCard } from './endpoint-card';
import {
  useIntegration,
  useEditIntegration,
  useInvalidateIntegrations,
  useReorderIntegration,
} from './integrations-hooks';
import { useIntegrationsApi } from './integrations-provider';
import type { Integration, IntegrationEndpoint, IntegrationWebhook } from './integrations-types';
import { useOpenAPIImportModal } from './modals/openapi-import';
import { useIntegrationEndpointCreateSidebar } from './sidebars/add-edit-endpoint';
import { useIntegrationWebhookCreateSidebar } from './sidebars/add-edit-webhook';
import {
  IntegrationProps,
  useAuthenticationSidebar,
  useAuthTypeLabel,
} from './sidebars/authentication';
import { WebhookCard } from './webhook-card';

export type IntegrationDetailProps = {
  integrationId?: string;
  backUrl?: string;
  extraTabs?: Array<RouterTab>;
  hasWebhooks?: boolean;
  onDismissSetupHelper?: () => void;
  hasHiddenSetupHelper?: boolean;
  portaledHeader?: boolean;
  headerExtras?: ReactNode;
};

export const IntegrationDetail = ({
  integrationId,
  backUrl,
  extraTabs,
  hasWebhooks,
  onDismissSetupHelper,
  hasHiddenSetupHelper = false,
  portaledHeader = false,
  headerExtras,
}: IntegrationDetailProps = {}) => {
  const { t } = useScopedTranslate(locale);
  const integrationQuery = useIntegration(integrationId!);

  if (!integrationQuery.data) {
    return <Spinner />;
  }
  const integration = integrationQuery.data;

  const tabs: RouterTab[] = [
    {
      content: (
        <Details
          integration={integration}
          onDismissSetupHelper={onDismissSetupHelper}
          hasHiddenSetupHelper={hasHiddenSetupHelper}
        />
      ),
      label: t('details'),
      section: 'details',
    },
    ...(extraTabs ?? []),
    {
      content: <IntegrationSettings integration={integration} />,
      label: t('settings'),
      section: 'settings',
      icon: <IconSettings />,
    },
  ];

  return (
    <RouterTabsHeader
      backAction={<BackAction backUrl={backUrl} />}
      actions={<HeaderActions integration={integration} hasWebhooks={hasWebhooks} />}
      breadcrumbs={
        <Stack orientation="horizontal" space={2} align="center">
          <Breadcrumb items={[{ label: integration.name }]} />
          {headerExtras}
        </Stack>
      }
      tabs={tabs}
      isPortaled={portaledHeader}
    />
  );
};

const BackAction = ({
  icon = <IconArrowLeft />,
  variant = 'ghost-purple',
  backUrl,
}: {
  icon?: JSX.Element;
  variant?: IconButtonProps['variant'];
  backUrl?: string;
}) => {
  if (!backUrl) {
    return null;
  }

  return (
    <Link to={backUrl}>
      <IconButton data-cy="journey-back-btn" icon={icon} variant={variant} />
    </Link>
  );
};

const HeaderActions = ({
  integration,
  hasWebhooks = false,
}: IntegrationProps & { hasWebhooks?: boolean }) => {
  const { t } = useScopedTranslate(locale);
  const showCreateEndpointSidebar = useIntegrationEndpointCreateSidebar();
  const showCreateWebhookSidebar = useIntegrationWebhookCreateSidebar();
  const showOpenAPIImportModal = useOpenAPIImportModal();
  const { parseOpenAPISpec, createEndpointsFromOpenAPI } = useIntegrationsApi();
  const { invalidateIntegrations } = useInvalidateIntegrations();

  const hasOpenAPIImport = Boolean(parseOpenAPISpec && createEndpointsFromOpenAPI);

  const handleOpenAPIImport = () => {
    if (!parseOpenAPISpec || !createEndpointsFromOpenAPI) {
      return;
    }
    showOpenAPIImportModal({
      integrationId: integration.shortId,
      currentBaseUrl: integration.baseUrl,
      onParse: (file) => parseOpenAPISpec(integration.shortId, file),
      onCreate: async (params) => {
        const result = await createEndpointsFromOpenAPI({
          integrationShortId: integration.shortId,
          endpoints: params.endpoints,
          updateBaseUrl: params.updateBaseUrl,
          baseUrl: params.baseUrl,
        });
        invalidateIntegrations();
        showSuccess(t('endpointsCreated', { count: result.createdCount }));
        return result;
      },
    });
  };

  return (
    <div>
      <Stack orientation="horizontal" space={2}>
        <Menu
          buttonProps={{
            text: t('addEndpoint'),
            iconLeft: <IconEndpoint />,
            iconRight: <IconDropdown />,
            variant: 'primary',
          }}
        >
          <MenuItem
            icon={<IconApi />}
            label={t('api')}
            onSelect={() => showCreateEndpointSidebar({ integrationId: integration.shortId })}
          />
          <MenuItem
            icon={<IconWebhook />}
            label={t('webhook')}
            extraIcon={
              hasWebhooks ? undefined : (
                <Stack align="center">
                  <BadgeSubscription />
                </Stack>
              )
            }
            extraIconFullOpacity
            onSelect={() => showCreateWebhookSidebar({ integrationId: integration.shortId })}
            isDisabled={!hasWebhooks}
          />
          {hasOpenAPIImport && (
            <>
              <MenuSeparator />
              <MenuItem
                icon={<IconUpload />}
                label={t('importFromOpenAPI')}
                onSelect={handleOpenAPIImport}
              />
            </>
          )}
        </Menu>
        <BackAction icon={<IconCloseRegular />} variant="secondary-purple" backUrl={undefined} />
      </Stack>
    </div>
  );
};

type LocalIntegrationItem = IntegrationItem<IntegrationEndpoint<string> | IntegrationWebhook>;

const Details = ({
  integration,
  onDismissSetupHelper,
  hasHiddenSetupHelper,
}: IntegrationProps & {
  onDismissSetupHelper?: () => void;
  hasHiddenSetupHelper?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const showAuthenticationSidebar = useAuthenticationSidebar();
  const authTypeLabel = useAuthTypeLabel(integration.authentication?.authType);
  const hasAuthentication = Boolean(integration.authentication?.isAuthenticated);
  const reorderMutation = useReorderIntegration(integration.shortId);

  const [items, setItems] = useState<LocalIntegrationItem[]>(() =>
    combineEndpointsAndWebhooks(integration.endpoints, integration.webhooks)
  );

  useEffect(() => {
    setItems(combineEndpointsAndWebhooks(integration.endpoints, integration.webhooks));
  }, [integration.endpoints, integration.webhooks]);

  const handleReorder = (activeIndex: number, overIndex: number) => {
    const previousItems = items;
    const newOrder = reorderList(items, activeIndex, overIndex);
    setItems(newOrder);
    reorderMutation.mutate(
      {
        items: newOrder.map((item) => ({
          type: item.type,
          id: item.data.shortId,
        })),
      },
      {
        onError: () => {
          setItems(previousItems);
        },
      }
    );
  };

  return (
    <PageContent>
      <Stack space={2}>
        <ListCard
          icon={
            <IconFancyContainer
              icon={<IconSSO color={hasAuthentication ? 'elementGreen100' : 'elementPurple50'} />}
              variant={hasAuthentication ? 'green' : 'gray'}
            />
          }
          spaceX={4}
          spaceY={4}
          title={t('authentication')}
          description={authTypeLabel}
          actions={
            <Button
              variant={hasAuthentication ? 'ghost' : 'primary-blue'}
              text={hasAuthentication ? t('core.edit') : t('authenticate')}
              onClick={() => showAuthenticationSidebar({ integration })}
            />
          }
        />
        {items.length > 0 ? (
          <SortableList
            onChange={handleReorder}
            items={items}
            idProperty="id"
            DraggableComponent={DraggableIntegrationItem}
            componentProps={{ baseUrl: integration.baseUrl }}
          />
        ) : (
          <Card variant="transparent" spaceX={6} spaceY={4}>
            <Stack orientation="horizontal" justify="space-between" align="center">
              <Stack orientation="horizontal" space={4} align="center">
                <IconEndpoint color="elementPurple60" />
                <Text font="semibold14" text={t('noEndpoint')} color="textSecondary" />
              </Stack>
            </Stack>
          </Card>
        )}
        <SetupHelper
          integration={integration}
          onDismiss={onDismissSetupHelper}
          hasHidden={hasHiddenSetupHelper}
        />
      </Stack>
    </PageContent>
  );
};

type DraggableIntegrationItemProps = DraggableComponentProps<LocalIntegrationItem> & {
  baseUrl?: string;
};

const DraggableIntegrationItem = ({
  item,
  attributes,
  listeners,
  baseUrl,
}: DraggableIntegrationItemProps) => {
  if (item.type === 'endpoint') {
    return (
      <EndpointCard
        endpoint={item.data as IntegrationEndpoint<string>}
        baseUrl={baseUrl}
        dragAttributes={attributes}
        dragListeners={listeners}
      />
    );
  }
  return (
    <WebhookCard
      webhook={item.data as IntegrationWebhook}
      dragAttributes={attributes}
      dragListeners={listeners}
    />
  );
};

const SetupHelper = ({
  integration,
  onDismiss,
  hasHidden,
}: {
  integration: Integration;
  onDismiss?: () => void;
  hasHidden?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);

  if (hasHidden) {
    return null;
  }

  const integrationStep = !integration.authentication
    ? 'connect'
    : integration.endpoints.length === 0
    ? 'addEndpoint'
    : 'configure';
  const isFinished = integration.endpoints.some(
    (endpoint) => Object.keys(endpoint.responseValues ?? {}).length > 0
  );
  const steps = [
    {
      id: 'connect',
      label: t('connect'),
      description: t('connectDescription'),
    },
    {
      id: 'addEndpoint',
      label: t('addEndpoint'),
      description: t('addEndpointDescription'),
    },
    {
      id: 'configure',
      label: t('configure'),
      description: t('configureDescription'),
    },
  ] as const;

  return (
    <StepWizard
      title={t('finishSetup')}
      steps={steps}
      currentStep={integrationStep}
      onDismiss={onDismiss}
      isFinished={isFinished}
    />
  );
};

const IntegrationSettings = ({ integration }: IntegrationProps) => {
  const { t } = useScopedTranslate(locale);
  const updateIntegration = useEditIntegration(() => showSuccess(t('integrationSaved')));
  return (
    <PageContent>
      <MiddleColumnPage>
        <RadioGroup
          label={t('emptyFieldHandlingLabel')}
          description={t('emptyFieldHandlingDescription')}
          defaultValue={integration.emptyFieldHandling}
          onChange={(value) =>
            updateIntegration.mutate({
              integrationId: integration.shortId,
              data: { emptyFieldHandling: value as Integration['emptyFieldHandling'] },
            })
          }
        >
          <RadioGroup.Option
            key="empty_string"
            label={t('emptyFieldHandlingEmptyString')}
            value="empty_string"
          />
          <RadioGroup.Option key="null" label={t('emptyFieldHandlingNull')} value="null" />
          <RadioGroup.Option key="skip" label={t('emptyFieldHandlingSkip')} value="skip" />
        </RadioGroup>
      </MiddleColumnPage>
    </PageContent>
  );
};

const locale = {
  en: {
    name: 'Name',
    description: 'Description',
    details: 'Details',
    journeys: 'Journeys',
    settings: 'Settings',
    addEndpoint: 'Add Endpoint',
    api: 'API',
    webhook: 'Webhook',
    add: 'Add',
    array: 'Array',
    field: 'Field',
    type: 'Type',
    label: 'Label',
    test: 'Test',
    authentication: 'Authentication',
    authenticate: 'Authenticate',
    responseValues: 'Response values',
    noResponseValues:
      'No values received, please add your test request values in the API call and click test',
    afterSuccessfulResponse:
      'Once you receive a successful response the mappable values will appear here',
    noEndpoint: 'No endpoints added',
    finishSetup: 'Finish setup',
    connect: 'Connect',
    connectDescription: 'Add authentication method and connect',
    addEndpointDescription: 'Add endpoints for your intended functions',
    configure: 'Configure & test',
    configureDescription: 'Add test data, test and receive mappable values',
    endpointCurrentlyUsed: 'This endpoint is currently being used in a journey',
    removeEndpoint: 'Remove endpoint',
    areYouSureDelete:
      'Are you sure you want to remove this endpoint? All your current configurations and response values will be lost\n\nThis can not be undone but you can always add it back later',
    noJourneys: 'No journeys',
    noJourneysDescription: 'This integration is currently not used in any journeys',
    emptyFieldHandlingLabel: 'Empty field handling',
    emptyFieldHandlingDescription:
      'Configure how empty fields are sent to this integration in your requests',
    emptyFieldHandlingEmptyString: 'Send empty string as empty ("") *Default',
    emptyFieldHandlingNull: 'Send empty strings as null',
    emptyFieldHandlingSkip: 'Skip empty fields in requests',
    integrationSaved: 'Integration saved',
    importFromOpenAPI: 'Import from OpenAPI',
    endpointsCreated: '{count} endpoint(s) created from OpenAPI spec',
  },
};
